import { useEffect } from 'react';
import { useStore, type EditorTab } from '../store';
import { useShortcutModifier } from '../hooks/useShortcutModifier';

export function Tabs() {
  const tab = useStore((s) => s.tab);
  const setTab = useStore((s) => s.setTab);
  const headerCount = useStore((s) => s.headers.length);
  const unreadCaptures = useStore((s) => s.unreadCaptures);
  const setHelpOpen = useStore((s) => s.setHelpOpen);
  const setPayloadsOpen = useStore((s) => s.setPayloadsOpen);
  const mod = useShortcutModifier();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't fire shortcuts while the user is typing. Monaco's hidden
      // textarea normally bubbles up as TEXTAREA, but the editor sometimes
      // re-dispatches with the .monaco-editor container as the target.
      const target = e.target as HTMLElement | null;
      const inField =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable ||
          target.closest('.monaco-editor') !== null);
      // Only the bare "?" is a typing hazard. Modifier combos are safe to
      // handle from inside fields; Monaco registers the same ones itself.
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (inField) return;
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.key === '1') {
        e.preventDefault();
        setTab('body');
      } else if (e.key === '2') {
        e.preventDefault();
        setTab('headers');
      } else if (e.key === '3') {
        e.preventDefault();
        setTab('captures');
      } else if (e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setPayloadsOpen(!useStore.getState().payloadsOpen);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setTab, setHelpOpen, setPayloadsOpen]);

  const captureLabel = unreadCaptures > 0 ? `Captures (${unreadCaptures})` : 'Captures';
  const tabs: { id: EditorTab; label: string; title: string }[] = [
    { id: 'body', label: 'Body', title: `Body editor (${mod}+1)` },
    { id: 'headers', label: `Headers (${headerCount})`, title: `Response headers (${mod}+2)` },
    { id: 'captures', label: captureLabel, title: `Capture endpoints (${mod}+3)` },
  ];

  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={tab === t.id}
          className={
            (tab === t.id ? 'active ' : '') +
            (t.id === 'captures' && unreadCaptures > 0 ? 'has-badge' : '')
          }
          onClick={() => setTab(t.id)}
          title={t.title}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
